import { AnchorButton, Heading, List, ListItem, Stack, Text } from '@instabox/consumer-web-ui'
import cn from 'classnames'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { Suspense } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { getContactHref, getLocationPartnerContact } from '../../constants/workWithUs'
import useAnalytics from '../../hooks/useAnalytics'
import type { HomePageProps } from '../../pages'
import PageContent from '../PageContent'
import PageSection from '../PageSection'
import Partner from '../Partner'
import PartnerGrid from '../PartnerGrid'
import TextWithImage from '../TextWithImage'
import styles from './homePage.module.scss'

const PhoneAnimation = dynamic(() => import('../PhoneAnimation'), { ssr: false })

export function HomePage({ partners, content, market }: HomePageProps) {
  const textWithImages = content.filter(({ item }) => item.__typename === 'texts_with_images')
  const [mainHero, flagHero] = content.filter(({ item }) => item.__typename === 'heroes')
  const images = content.filter(({ item }) => item.__typename === 'images')

  return (
    <>
      {!!mainHero && <MainHeroSection mainHero={mainHero} market={market} />}
      <AboutUsSection />
      {partners.length > 0 && <PartnersSection partners={partners} />}
      <WorkWithUsSection images={images} market={market} />
      {textWithImages.length > 0 && <TextWithImageSection textWithImages={textWithImages} />}
      {!!flagHero && <FlagHeroSection flagHero={flagHero} />}
    </>
  )
}

function MainHeroSection({
  mainHero,
  market,
}: {
  mainHero: HomePageProps['content'][number]
  market: HomePageProps['market']
}) {
  const salesFormLink = getSalesFormLink(market.code)

  return (
    <PageContent fields={['heroes']} content={[mainHero]}>
      <Stack gap="lg">
        <Heading color="white" align="center" italic>
          <FormattedMessage id="home.title" />
        </Heading>
        <div className="row justify-content-center">
          <div className="col-auto">
            <Link href="/help" passHref legacyBehavior>
              <AnchorButton size="large" variant="solid">
                <FormattedMessage id="home.help" />
              </AnchorButton>
            </Link>
          </div>
          {!!salesFormLink && (
            <div className="col-auto">
              <SalesFormButton href={salesFormLink} marketCode={market.code} />
            </div>
          )}
        </div>
      </Stack>
    </PageContent>
  )
}

function SalesFormButton({ marketCode, href }: { marketCode: string; href: string }) {
  const sendAnalyticsEvent = useAnalytics()

  const trackEvent = () => {
    sendAnalyticsEvent('ship_with_us', { market: marketCode.toUpperCase() })
  }

  return (
    <AnchorButton
      onClick={trackEvent}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      size="large"
      variant="solid">
      <FormattedMessage id="home.sales" />
    </AnchorButton>
  )
}

function getSalesFormLink(marketCode: string) {
  switch (marketCode.toUpperCase()) {
    case 'SE':
      return 'https://forms.gle/LYzDRF9suHEn5FbDA'
    case 'NO':
      return 'https://forms.gle/EsDNjFhdj78SaHqc7'
    case 'DK':
      return 'https://forms.gle/CTpXUT6aWGtSct469'
    default:
      return null
  }
}

function AboutUsSection() {
  return (
    <PageSection backgroundColor="var(--color-pale-pink)">
      <div className={cn('row align-items-center', styles.row)}>
        <div className="col-12 col-md-6">
          <Stack gap="lg">
            <Stack gap="sm">
              <Heading color="really-red" fontSize="xl">
                <FormattedMessage id="home.what_is_instabox.title" />
              </Heading>
              <Text>
                <FormattedMessage id="home.what_is_instabox.subtitle" />
              </Text>
            </Stack>
            <Stack gap="sm">
              <Heading as="label" color="really-red">
                <FormattedMessage id="home.what_is_instabox.list.label" />
              </Heading>
              <List variant="secondary">
                <ListItem count={1} variant="secondary">
                  <FormattedMessage id="home.what_is_instabox.list.item_1" />
                </ListItem>
                <ListItem count={2} variant="secondary">
                  <FormattedMessage id="home.what_is_instabox.list.item_2" />
                </ListItem>
                <ListItem count={3} variant="secondary">
                  <FormattedMessage id="home.what_is_instabox.list.item_3" />
                </ListItem>
              </List>
            </Stack>
          </Stack>
        </div>
        <Suspense fallback="Loading...">
          <div className="col-12 col-md-6">
            <PhoneAnimation />
          </div>
        </Suspense>
      </div>
    </PageSection>
  )
}

function PartnersSection({ partners }: { partners: HomePageProps['partners'] }) {
  return (
    <PageSection backgroundColor="var(--color-better-blush)">
      <Heading align="center" fontSize="xl" color="really-red">
        <FormattedMessage id="home.partners.subtitle" />
      </Heading>
      <Text align="center" fontWeight="medium">
        <FormattedMessage id="home.partners.title" />
      </Text>
      <PartnerGrid>
        {partners.map(partner => (
          <Partner key={partner.id} {...partner} />
        ))}
      </PartnerGrid>
    </PageSection>
  )
}

function WorkWithUsSection({
  images,
  market,
}: {
  images: HomePageProps['content']
  market: HomePageProps['market']
}) {
  const { formatMessage } = useIntl()
  const { phone, email } = getLocationPartnerContact(market.code)
  const locationsFormHref = getContactHref(market.locations_email)
  const salesFormLink = getSalesFormLink(market.code)

  // The home CMS page holds one image per block: the first for location partners, the second for merchants
  const [locationsImage, merchantsImage] = images.map(({ item }) =>
    item.__typename === 'images' ? item.translations?.[0]?.image || item.image : null,
  )

  return (
    <>
      <TextWithImage
        id="locations"
        image={locationsImage}
        squareImage
        image_placement="left"
        layout="fullBleed"
        eyebrow={formatMessage({ id: 'work_with_us.locations.eyebrow' })}
        header={formatMessage({ id: 'work_with_us.locations.title' })}
        text={formatMessage(
          { id: 'work_with_us.locations.text' },
          {
            phone: `[${phone}](tel:${phone.replace(/\s/g, '')})`,
            email: `[${email}](mailto:${email})`,
          },
        )}
        action={
          !!locationsFormHref && (
            <AnchorButton
              href={locationsFormHref}
              target="_blank"
              rel="noopener noreferrer"
              size="large"
              variant="secondary">
              <FormattedMessage id="work_with_us.locations.button" />
            </AnchorButton>
          )
        }
      />
      <TextWithImage
        id="merchants"
        image={merchantsImage}
        squareImage
        image_placement="right"
        layout="fullBleed"
        eyebrow={formatMessage({ id: 'work_with_us.merchants.eyebrow' })}
        header={formatMessage({ id: 'work_with_us.merchants.title' })}
        text={formatMessage({ id: 'work_with_us.merchants.text' })}
        action={
          !!salesFormLink && (
            <AnchorButton
              href={salesFormLink}
              target="_blank"
              rel="noopener noreferrer"
              size="large"
              variant="secondary">
              <FormattedMessage id="work_with_us.merchants.button" />
            </AnchorButton>
          )
        }
      />
    </>
  )
}

function TextWithImageSection({ textWithImages }: { textWithImages: HomePageProps['content'] }) {
  return (
    <PageSection gap="xl">
      <Heading align="center" color="white" fontSize="xl">
        <FormattedMessage id="home.why_instabox.header" />
      </Heading>
      <div className="row justify-content-center">
        {textWithImages.map(({ item }) => {
          if (item.__typename === 'texts_with_images') {
            return (
              <div className="col-12 col-md-6 col-lg-4" key={item.id}>
                {/* @ts-ignore */}
                <TextWithImage {...item} layout="card" />
              </div>
            )
          }
          return null
        })}
      </div>
    </PageSection>
  )
}

function FlagHeroSection({ flagHero }: { flagHero: HomePageProps['content'][number] }) {
  return (
    <PageContent fields={['heroes']} content={[flagHero]}>
      <Stack>
        <Heading align="center" color="really-red" fontSize="xl">
          <FormattedMessage id="home.find_us.title" />
        </Heading>
        <div className="row justify-content-center">
          <div className="col-auto">
            <AnchorButton size="large" href="/places">
              <FormattedMessage id="home.find_us.button" />
            </AnchorButton>
          </div>
        </div>
      </Stack>
    </PageContent>
  )
}
